import { getOwnerName, strings } from "@quickpoint.me/shared";
import { getAuth, getIdToken } from "firebase/auth";
import {
  AuthScreenAtom,
  Authorization,
  CreatingFirebaseAccount,
  FirebaseAccountCreated,
  FirebaseErrorAtom,
  IsCheckingOwnerName,
  NetworkError,
} from "./authAtoms";
import { DemoMode, ShowRefreshSpinner } from "./uiAtoms";

import { selector } from "recoil";

/** The URL slug for the current auth screen; false when there is none. */
export const CurrentAuthURL = selector<string | false>({
  key: "CurrentAuthURL",
  get: ({ get }) => {
    switch (get(AuthScreenAtom)) {
      case "loginChoices":
        return strings.url.showLogin;
      case "loginEmail":
        return strings.url.userRequestsLoginWithEmail;
      case "signUp":
        return strings.url.showSignUp;
      case "signUpEmail":
        return strings.url.userRequestsSignUpWithEmail;
      default:
        return false;
    }
  },
});

export const initializingFireBaseUser = selector<boolean>({
  key: "initializingFireBaseUser",
  get: ({ get }) => {
    const auth = get(Authorization);
    const init = auth.initializingFireBaseUser;
    return init;
  },
});

// a utility selector to set all the relevant states
// when a user logs in. The value of this will never be accessed in the app
export const LoginSuccessfulResetState = selector<true>({
  key: "LoginSuccessful",
  get: () => {
    return true;
  },
  set: ({ reset }) => {
    reset(AuthScreenAtom);
    reset(CreatingFirebaseAccount);
    reset(FirebaseAccountCreated);
    reset(IsCheckingOwnerName);
    reset(NetworkError);
    reset(ShowRefreshSpinner);
    reset(FirebaseErrorAtom);
  },
});


export const OwnerId = selector<string | undefined>({
  key: "OwnerId",
  get: ({ get }) => {
    const auth = get(Authorization);
    return auth.uid;
  },
});

export const OwnerName = selector<string | undefined>({
  key: "OwnerName",
  get: async ({ get }) => {
    const demoMode = get(DemoMode)
    if (demoMode) return "demo";
    const user = getAuth().currentUser
    const auth = get(Authorization)
    if (!auth.uid || !user) return strings.common.unauthorized;

    const jwt = await getIdToken(user);
    // else get it from the KV store
    const getOwner = await getOwnerName(jwt);
    if (getOwner.owners) {
      if (getOwner.owners.length === 0) {
        // oops the user hasn't registered a name.
        // this can happen by logging in with OAuth.. Firebase creates an account anyway.
        return strings.utility.noOwnerNameAssociatedWithEmail;
      }
      return getOwner.owners[0];
    }
    // no alert here — selectors must stay side-effect free; the throw
    // surfaces through the Suspense/error boundary
    throw new Error("OwnerName: server error");
  },
});

export const OwnerNameAssociatedWithEmail = selector<boolean>({
  key: "OwnerNameAssociatedWithEmail",
  get: async ({ get }) => {
    const ownerName = get(OwnerName);
    if (ownerName === strings.utility.noOwnerNameAssociatedWithEmail) {
      return false;
    }
    return true;
  },
});

export const ShowAuthorizationSelector = selector<boolean | typeof strings.utility.loading>({
  key: "ShowAuthorizationSelector",
  get: ({ get }) => {
    const authorisation = get(Authorization);
    return !authorisation.isLoggedIn;
  },
});
